import { getSession } from '../../app/auth/authAdapter';
import {
  getDashboardCacheErrorCategory,
  reportDashboardCacheDiagnostic,
} from './dashboard.cache.diagnostic';
import type { DashboardOverview } from './types/dashboard.types';

const STORAGE_KEY = 'vania.super-admin.dashboard.snapshot';
const CACHE_VERSION = 1;
const CACHE_TTL_MS = 5 * 60 * 1000;

interface DashboardSnapshot {
  version: number;
  timestamp: number;
  userKey: string;
  dashboard: DashboardOverview;
}

function getUserKey(): string | null {
  const session = getSession();

  if (!session) {
    return null;
  }

  return `${session.user.mobile}:${session.activeContextId}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function hasNumbers(value: unknown, keys: string[]): boolean {
  return isRecord(value) && keys.every((key) => typeof value[key] === 'number');
}

function isDashboardOverview(value: unknown): value is DashboardOverview {
  if (!isRecord(value)) {
    return false;
  }

  const statusDistribution = value.orders;
  const representatives = value.representatives;
  const charts = value.charts;

  return (
    hasNumbers(value.overview, [
      'total_users',
      'active_users',
      'new_users',
      'total_revenue',
      'total_orders',
      'total_transactions',
    ]) &&
    hasNumbers(value.wallet, ['total_topups', 'deposit_volume', 'withdraw_volume']) &&
    hasNumbers(value.commission, ['total_commission', 'paid_commission', 'pending_commission']) &&
    hasNumbers(statusDistribution, ['order_count', 'revenue']) &&
    isRecord(statusDistribution) &&
    Array.isArray(statusDistribution.status_distribution) &&
    statusDistribution.status_distribution.every(
      (item) => isRecord(item) && typeof item.status === 'string' && typeof item.count === 'number',
    ) &&
    isRecord(value.tenants) &&
    hasNumbers(value.tenants, ['total_tenants', 'active_tenants']) &&
    isRecord(representatives) &&
    typeof representatives.total_representatives === 'number' &&
    Array.isArray(representatives.performance_summary) &&
    representatives.performance_summary.every(
      (item) =>
        isRecord(item) &&
        typeof item.representative_id === 'number' &&
        (typeof item.full_name === 'string' || item.full_name === null) &&
        typeof item.orders === 'number' &&
        typeof item.revenue === 'number',
    ) &&
    isRecord(charts) &&
    Array.isArray(charts.revenue_trend) &&
    charts.revenue_trend.every(
      (item) => isRecord(item) && typeof item.period === 'string' && typeof item.value === 'number',
    ) &&
    Array.isArray(charts.orders_trend) &&
    charts.orders_trend.every(
      (item) => isRecord(item) && typeof item.period === 'string' && typeof item.value === 'number',
    ) &&
    Array.isArray(charts.user_growth_trend) &&
    charts.user_growth_trend.every(
      (item) => isRecord(item) && typeof item.period === 'string' && typeof item.users === 'number',
    )
  );
}

export function readDashboardSnapshot(): DashboardOverview | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const userKey = getUserKey();
  if (!userKey) {
    reportDashboardCacheDiagnostic('missing_data');
    return null;
  }

  let storageReadSucceeded = false;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    storageReadSucceeded = true;
    reportDashboardCacheDiagnostic('storage_available');
    if (!raw) {
      reportDashboardCacheDiagnostic('read_not_found');
      return null;
    }
    reportDashboardCacheDiagnostic('read_found');

    let snapshot: Partial<DashboardSnapshot>;
    try {
      snapshot = JSON.parse(raw) as Partial<DashboardSnapshot>;
      reportDashboardCacheDiagnostic('parse_success');
    } catch {
      reportDashboardCacheDiagnostic('parse_failure');
      throw new Error('Invalid dashboard cache JSON');
    }
    const isExpired =
      typeof snapshot.timestamp !== 'number' ||
      Date.now() - snapshot.timestamp > CACHE_TTL_MS;

    if (snapshot.version !== CACHE_VERSION) {
      reportDashboardCacheDiagnostic('validation_version');
      window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    if (isExpired) {
      reportDashboardCacheDiagnostic('validation_ttl');
      window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    if (snapshot.userKey !== userKey) {
      reportDashboardCacheDiagnostic('validation_user_key');
      window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    if (!isDashboardOverview(snapshot.dashboard)) {
      reportDashboardCacheDiagnostic('validation_payload');
      window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return snapshot.dashboard;
  } catch {
    if (!storageReadSucceeded) {
      reportDashboardCacheDiagnostic('storage_unavailable');
    }
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Storage failures must never block the live dashboard request.
    }
    return null;
  }
}

export function writeDashboardSnapshot(dashboard: DashboardOverview): void {
  reportDashboardCacheDiagnostic('write_function_entered');

  if (typeof window === 'undefined') {
    reportDashboardCacheDiagnostic('session_available', false);
    reportDashboardCacheDiagnostic('user_key_available', false);
    return;
  }

  const session = getSession();
  reportDashboardCacheDiagnostic('session_available', Boolean(session));
  const userKey = session
    ? `${session.user.mobile}:${session.activeContextId}`
    : null;
  reportDashboardCacheDiagnostic('user_key_available', Boolean(userKey));
  if (!userKey) {
    reportDashboardCacheDiagnostic('missing_data');
    return;
  }

  let storage: Storage;
  try {
    storage = window.sessionStorage;
    reportDashboardCacheDiagnostic('storage_available', true);
  } catch (error) {
    reportDashboardCacheDiagnostic('storage_available', false);
    reportDashboardCacheDiagnostic('set_item_result', getDashboardCacheErrorCategory(error));
    reportDashboardCacheDiagnostic('write_failure');
    // A full or unavailable cache must never block the live response.
    return;
  }

  try {
    reportDashboardCacheDiagnostic('set_item_attempted');
    reportDashboardCacheDiagnostic('write_attempted');
    const snapshot: DashboardSnapshot = {
      version: CACHE_VERSION,
      timestamp: Date.now(),
      userKey,
      dashboard,
    };
    storage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    reportDashboardCacheDiagnostic('set_item_result', 'success');
    reportDashboardCacheDiagnostic('write_success');
  } catch (error) {
    reportDashboardCacheDiagnostic('set_item_result', getDashboardCacheErrorCategory(error));
    reportDashboardCacheDiagnostic('write_failure');
    return;
  }

  try {
    reportDashboardCacheDiagnostic('post_write_key_exists', storage.getItem(STORAGE_KEY) !== null);
  } catch (error) {
    reportDashboardCacheDiagnostic('post_write_key_exists', getDashboardCacheErrorCategory(error));
  }
}
